import { z } from 'zod';
import { AssetIdSchema, AssetRefSchema } from './assetRef';
// 共通: 位置
export const PositionSchema = z.object({
    x: z.string().describe('X座標（%またはpx）'),
    y: z.string().describe('Y座標（%またはpx)')
});
// 共通: アセット参照
// NR4: SSoT は assetRef.ts。ここは後方互換のための re-export（新規参照は assetRef を直接 import 推奨）。
export { AssetRefSchema } from './assetRef';
/**
 * 新規テンプレ10/11向け: assetIdのみ許可（URL直保存禁止）
 * NR4 注記: 名前に反して「単一のアセット参照」ではなく、新テンプレ 10-18 の
 * `assets` フィールドが使う assets バンドル（背景/BGM/画像群の束）。
 * 各フィールドは AssetIdSchema（= z.string().uuid() と同値）で再構成済み・parse 挙動は不変。
 */
export const AssetIdRefSchema = z.object({
    backgroundAssetId: AssetIdSchema.optional(),
    bgmAssetId: AssetIdSchema.optional(),
    imageAssetIds: z.array(AssetIdSchema).optional()
});
// 共通フィールド
export const BaseConfigSchema = z.object({
    background: z
        .object({
        type: z.enum(['color', 'image', 'video']).default('color'),
        value: z.string().default('#1a1a24'),
        overlay: z.string().optional()
    })
        .optional(),
    audio: z
        .object({
        bgm: AssetRefSchema.optional(),
        successSE: AssetRefSchema.optional(),
        failSE: AssetRefSchema.optional(),
        tapSE: AssetRefSchema.optional()
    })
        .optional(),
    timeLimitMs: z.number().min(0).optional(),
    showTimer: z.boolean().default(true),
    maxMistakes: z.number().min(0).optional(),
    showMistakeCount: z.boolean().default(true),
    successMessage: z
        .object({
        title: z.string().default('成功！'),
        subtitle: z.string().optional(),
        showScore: z.boolean().default(false)
    })
        .optional(),
    failMessage: z
        .object({
        title: z.string().default('失敗...'),
        subtitle: z.string().optional(),
        allowRetry: z.boolean().default(true)
    })
        .optional(),
    hints: z
        .object({
        enabled: z.boolean().default(true),
        maxUses: z.number().default(3),
        cooldownMs: z.number().default(10000),
        messages: z.array(z.string()).default([])
    })
        .optional()
});
// テンプレ1 捜索
export const InvestigationConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(1),
    // 縦写真など、元画像の座標系を画面比率に左右されず保つ任意設定。
    // 指定時は探索地点と鮮明な背景をこの比率の中央キャンバス内へ配置する。
    coordinateAspectRatio: z.number().min(0.25).max(4).optional(),
    // mode: 'ordered' = 順番通り（デフォルト）, 'unordered' = 順不同で全てタップ
    mode: z.enum(['ordered', 'unordered']).default('ordered'),
    items: z.array(z.object({
        id: z.string(),
        label: z.string(),
        description: z.string().optional(), // タップ時に表示する説明
        position: PositionSchema,
        size: z
            .object({
            width: z.string().default('64px'),
            height: z.string().default('64px')
        })
            .optional(),
        image: AssetRefSchema.optional(),
        highlightOnHover: z.boolean().default(true),
        glowEffect: z.boolean().default(false)
    })),
    // orderedモード: 正しい順番、unorderedモード: 収集対象アイテム（指定なしの場合はcorrectOrderを使用）
    correctOrder: z.array(z.string()),
    targetItems: z.array(z.string()).optional(), // unorderedモード用: 収集対象アイテムID
    orderHint: z.object({
        text: z.string(),
        showAtStart: z.boolean().default(true)
    }),
    stepFeedback: z
        .array(z.object({
        onCorrect: z.string().optional(),
        onWrong: z.string().optional()
    }))
        .optional(),
    showFoundItems: z.boolean().default(true),
    foundItemStyle: z.enum(['checkmark', 'glow', 'fade']).default('checkmark'),
    // showItemMarkers: 枠とラベルを表示するか（falseでも当たり判定は有効）
    showItemMarkers: z.boolean().default(true),
    // instructionText: 画面タップを促す説明文
    instructionText: z.string().optional()
});
// テンプレ2 指紋採取
// 2026-06-12 R0 監査: 実装（Template02Fingerprint.jsx）準拠キー（title/instructionText）を optional 追加
//（minor・schemaVersion 1 維持・非破壊）。不在時は実装側既定値が効くため .default() は付けない。
export const FingerprintConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(2),
    /** ヘッダー見出し（2026-06-12 R0 追加・実装既定: '指紋採取'） */
    title: z.string().optional(),
    /** 手順説明文（2026-06-12 R0 追加・実装既定: '正しい手順で指紋を採取せよ'） */
    instructionText: z.string().optional(),
    tools: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        icon: z.string().optional(),
        image: AssetRefSchema.optional(),
        description: z.string().optional()
    }))
        .default([]),
    correctOrder: z.array(z.string()).default([]),
    fingerprintImage: AssetRefSchema.optional(),
    stageVisuals: z
        .array(z.object({
        afterToolId: z.string(),
        overlayColor: z.string().optional(),
        overlayOpacity: z.number().min(0).max(1).optional(),
        animation: z.enum(['none', 'pulse', 'glow']).optional(),
        message: z.string().optional()
    }))
        .optional(),
    toolbeltPosition: z.enum(['right', 'bottom']).default('right')
});
// テンプレ3 暗号ダイヤル
// 2026-06-12 R0 監査: 実装（Template03Cipher.jsx）準拠キー（title/instructionText）を optional 追加
//（minor・schemaVersion 1 維持・非破壊）。不在時は実装側既定値が効くため .default() は付けない。
export const CipherConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(3),
    /** ヘッダー見出し（2026-06-12 R0 追加・実装既定: '🔐 金庫解錠'） */
    title: z.string().optional(),
    /** ダイヤル上の説明文（2026-06-12 R0 追加・実装既定: 非表示） */
    instructionText: z.string().optional(),
    correctCode: z.array(z.number().min(0).max(9)).default([0, 0, 0]),
    dialCount: z.number().min(2).max(6).default(3),
    codeHints: z
        .array(z.object({
        text: z.string(),
        targetDigit: z.number(),
        highlightValue: z.number().optional()
    }))
        .default([]),
    dialStyle: z
        .object({
        backgroundColor: z.string().default('#1a1a24'),
        activeColor: z.string().default('#10B981'),
        textColor: z.string().default('#ffffff')
    })
        .optional(),
    showMatchCount: z.boolean().default(true),
    safeImage: AssetRefSchema.optional()
});
// テンプレ4 タイムライン再構成
export const TimelineConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(4),
    events: z
        .array(z.object({
        id: z.string(),
        text: z.string(),
        icon: z.string().optional(),
        timestamp: z.string().optional()
    }))
        .default([]),
    correctOrder: z.array(z.string()).default([]),
    constraints: z
        .array(z.object({
        text: z.string(),
        type: z.enum(['before', 'after', 'adjacent']).optional()
    }))
        .optional(),
    initialOrder: z.array(z.string()).optional(),
    reorderStyle: z.enum(['arrows', 'drag']).default('arrows')
});
// テンプレ5 証拠ボード
export const EvidenceBoardConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(5),
    nodes: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        position: PositionSchema,
        icon: z.string().optional(),
        image: AssetRefSchema.optional(),
        description: z.string().optional()
    }))
        .default([]),
    correctConnections: z
        .array(z.tuple([z.string(), z.string()]))
        .default([]),
    connectionStyle: z
        .object({
        color: z.string().default('#F59E0B'),
        width: z.number().default(2),
        dashed: z.boolean().default(false)
    })
        .optional(),
    wrongConnectionBehavior: z.enum(['remove', 'shake', 'keep']).default('shake')
});
// テンプレ6 二画面照合
export const ComparisonConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(6),
    currentImage: AssetRefSchema.default({ url: '' }),
    pastImage: AssetRefSchema.default({ url: '' }),
    differences: z
        .array(z.object({
        id: z.string(),
        label: z.string().optional(),
        position: PositionSchema,
        size: z
            .object({
            width: z.string().default('48px'),
            height: z.string().default('48px')
        })
            .optional(),
        showInPast: z.boolean().optional(),
        highlightOnFind: z.boolean().optional()
    }))
        .default([]),
    layout: z.enum(['vertical', 'horizontal', 'overlay']).default('vertical'),
    missTapBehavior: z.enum(['none', 'shake', 'penalize']).default('shake'),
    missTapPenalty: z.number().optional()
});
// テンプレ7 矛盾発見
export const ContradictionConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(7),
    facts: z
        .array(z.object({
        id: z.string(),
        text: z.string(),
        source: z.string().optional()
    }))
        .default([]),
    statements: z
        .array(z.object({
        id: z.string(),
        text: z.string(),
        speakerId: z.string().optional(),
        speakerName: z.string().optional(),
        isContradiction: z.boolean(),
        contradictionExplanation: z.string().optional()
    }))
        .default([]),
    displayMode: z.enum(['list', 'dialog', 'cards']).default('list'),
    wrongSelectionMessage: z.string().optional()
});
// テンプレ8 追跡（レーン）
export const ChaseConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(8),
    laneCount: z.number().min(2).max(5).default(3),
    initialDistance: z.number().default(50),
    goalDistance: z.number().default(100),
    escapeDistance: z.number().default(0),
    obstacles: z
        .object({
        frequency: z.number().min(0).max(1).default(0.3),
        speed: z.number().default(6),
        damage: z.number().default(10),
        types: z
            .array(z.object({
            id: z.string(),
            emoji: z.string().optional(),
            width: z.number().default(1)
        }))
            .default([])
    })
        .optional(),
    dash: z
        .object({
        enabled: z.boolean().default(true),
        boost: z.number().default(15),
        cooldownMs: z.number().default(2000)
    })
        .optional(),
    playerEmoji: z.string().default('🏃'),
    targetEmoji: z.string().default('🎯'),
    scrollSpeed: z.number().default(6)
});
// テンプレ9 緊迫チェイス（QTE）
export const IntenseChaseConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(9),
    defaultMode: z.enum(['chase', 'escape']).default('chase'),
    allowModeSelect: z.boolean().default(true),
    maxRounds: z.number().min(1).max(10).default(5),
    initialDistance: z.number().default(50),
    successDistance: z.number().default(100),
    failDistance: z.number().default(0),
    roundTimeLimitMs: z.number().default(2000),
    countdownMs: z.number().default(800),
    correctBonus: z.number().default(10),
    wrongPenalty: z.number().default(10),
    timeoutPenalty: z.number().default(5),
    directions: z.array(z.enum(['up', 'down', 'left', 'right'])).default(['up', 'down', 'left', 'right']),
    directionColors: z
        .object({
        up: z.string().default('#f59e0b'),
        down: z.string().default('#38bdf8'),
        left: z.string().default('#a855f7'),
        right: z.string().default('#22c55e')
    })
        .optional(),
    roundMessages: z
        .array(z.object({
        round: z.number(),
        preMessage: z.string().optional(),
        successMessage: z.string().optional(),
        failMessage: z.string().optional()
    }))
        .default([])
});
// テンプレ10: 捜査（調査）
export const InvestigationV2ConfigSchema = z.object({
    templateId: z.literal(10),
    title: z.string().optional(),
    locations: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        description: z.string().optional()
    }))
        .default([]),
    clues: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        locationId: z.string()
    }))
        .default([]),
    targetClueId: z.string().optional(),
    maxMistakes: z.number().min(0).optional(),
    timeLimitSec: z.number().min(1).max(600).optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ11: 矛盾突き付け
export const ContradictionV2ConfigSchema = z.object({
    templateId: z.literal(11),
    title: z.string().optional(),
    statements: z
        .array(z.object({
        id: z.string(),
        text: z.string()
    }))
        .default([]),
    evidence: z
        .array(z.object({
        id: z.string(),
        text: z.string()
    }))
        .default([]),
    correctPairs: z
        .array(z.object({
        statementId: z.string(),
        evidenceId: z.string()
    }))
        .default([]),
    maxMistakes: z.number().min(0).optional(),
    timeLimitSec: z.number().min(1).max(600).optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ12: 尋問（Interrogation）
export const InterrogationConfigSchema = z.object({
    templateId: z.literal(12),
    title: z.string().optional(),
    suspect: z.object({
        name: z.string(),
        avatar: z.string().optional(),
        initialTrust: z.number().min(0).max(100).default(30),
        initialStress: z.number().min(0).max(100).default(50),
        initialDefense: z.number().min(0).max(100).default(50)
    }),
    phases: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        dialogue: z.array(z.object({
            speaker: z.string(),
            text: z.string(),
            emotion: z.string().optional()
        })),
        options: z.array(z.object({
            text: z.string(),
            effect: z.object({
                trust: z.number().optional(),
                stress: z.number().optional(),
                defense: z.number().optional()
            }),
            response: z.string(),
            flag: z.string().optional()
        })),
        unlockCondition: z
            .object({
            trust: z.number().optional()
        })
            .optional()
    }))
        .default([]),
    confessionThreshold: z.number().default(70),
    breakdownThreshold: z.number().default(90),
    assets: AssetIdRefSchema.optional()
});
// テンプレ13: 推理クイズ（Deduction）
export const DeductionConfigSchema = z.object({
    templateId: z.literal(13),
    title: z.string().optional(),
    questions: z
        .array(z.object({
        id: z.string(),
        question: z.string(),
        options: z.array(z.string()),
        correct: z.number(),
        hint: z.string().optional(),
        explanation: z.string().optional()
    }))
        .default([]),
    passingScore: z.number().min(0).max(100).default(75),
    timeLimitSec: z.number().optional(),
    conclusion: z.string().optional(),
    successTitle: z.string().optional(),
    failureTitle: z.string().optional(),
    successIcon: z.string().optional(),
    failureIcon: z.string().optional(),
    completeButtonLabel: z.string().optional(),
    retryButtonLabel: z.string().optional(),
    failSubmitButtonLabel: z.string().optional(),
    visualTheme: z.enum(['default', 'soma']).optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ14: 暗号解読V2（CipherV2）
export const CipherV2ConfigSchema = z.object({
    templateId: z.literal(14),
    title: z.string().optional(),
    originalMessage: z.string(),
    cipherType: z.enum(['substitution', 'caesar', 'vigenere']),
    hints: z
        .array(z.object({
        plain: z.string(),
        hint: z.string()
    }))
        .default([]),
    maxMistakes: z.number().default(5),
    decodedMessage: z.string().optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ15: 選択の恐怖（DoorSelection）
// 2026-06-12 R0 監査: 実装（Template15DoorSelection.jsx）準拠キーを optional 追加（minor・schemaVersion 1 維持・非破壊）。
// 不在時は実装側既定値が効くため .default() は付けない。
export const DoorSelectionConfigSchema = z.object({
    templateId: z.literal(15),
    title: z.string().optional(),
    instruction: z.string().default('正しい扉を選んでください'),
    doors: z
        .array(z.object({
        id: z.string(),
        label: z.string(),
        /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
        description: z.string().optional(),
        /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
        image: AssetRefSchema.optional(),
        isCorrect: z.boolean().default(false),
        /** 間違いの扉を開けた時に表示する文言（2026-06-12 R0 追加・実装既定はランダム文言） */
        wrongBodyMessage: z.string().optional(),
        /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
        penalty: z
            .object({
            type: z.enum(['shake', 'sound', 'damage', 'custom']).default('shake'),
            message: z.string().optional(),
            damageAmount: z.number().optional(),
            customEffect: z.string().optional()
        })
            .optional()
    }))
        .default([]),
    timeLimitSec: z.number().min(0).optional(),
    showTimer: z.boolean().default(true),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    hintText: z.string().optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    hintDelaySec: z.number().default(10),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    wrongChoiceSFX: AssetRefSchema.optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    correctChoiceSFX: AssetRefSchema.optional(),
    maxMistakes: z.number().min(1).optional(),
    /** 心拍音演出の有効化（2026-06-12 R0 追加・実装既定: true） */
    heartbeatEnabled: z.boolean().optional(),
    /** 扉を開ける前の確認ダイアログ（2026-06-12 R0 追加・実装既定: true） */
    confirmBeforeOpen: z.boolean().optional(),
    /** 成功時メッセージ（2026-06-12 R0 追加・実装既定: ''） */
    successMessage: z.string().optional(),
    /** 失敗時メッセージ（2026-06-12 R0 追加・実装既定: 'もう...見つけられない...'） */
    failureMessage: z.string().optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ16: 存在の侵食（ExistenceErosion）
// 2026-06-12 R0 監査: 実装（Template16ExistenceErosion.jsx）は wave／抵抗ウィンドウ型で、
// 旧設計キー（barLabel/initialValue/decreaseSpeed/thresholds/erosionVisual/targetVariable/stopButtonLabel）は実装未消費。
// 旧キーは非破壊のため deprecated 残置し、実装準拠キーを optional 追加（minor・schemaVersion 1 維持）。
// 不在時は実装側既定値が効くため .default() は付けない。
export const ExistenceErosionConfigSchema = z.object({
    templateId: z.literal(16),
    title: z.string().optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    barLabel: z.string().default('存在度'),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    initialValue: z.number().min(0).max(100).default(100),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    decreaseSpeed: z.number().min(1).max(100).default(20),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    thresholds: z
        .array(z.object({
        value: z.number().min(0).max(100),
        result: z.enum(['success', 'partial', 'fail']),
        message: z.string().optional()
    }))
        .default([
        { value: 70, result: 'success', message: '完全に保たれた' },
        { value: 40, result: 'partial', message: '一部が侵食された' },
        { value: 0, result: 'fail', message: '存在が消えた...' }
    ]),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    erosionVisual: z
        .object({
        type: z.enum(['darkness', 'static', 'blur', 'custom']).default('darkness'),
        color: z.string().default('#000000'),
        intensity: z.number().min(0).max(1).default(0.8)
    })
        .optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    targetVariable: z.string().optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    stopButtonLabel: z.string().default('止める'),
    countdownSec: z.number().default(3),
    /** 画面上の指示文（2026-06-12 R0 追加・実装既定: '赤く光った瞬間に抵抗'） */
    instruction: z.string().optional(),
    /** 侵食ウェーブの総数（2026-06-12 R0 追加・実装既定: 6） */
    totalWaves: z.number().optional(),
    /** 1 ウェーブの長さ ms（2026-06-12 R0 追加・実装既定: 2000） */
    waveDurationMs: z.number().optional(),
    /** 抵抗入力の受付ウィンドウ ms（2026-06-12 R0 追加・実装既定: 800） */
    resistWindowMs: z.number().optional(),
    /** この欠損数で fail（2026-06-12 R0 追加・実装既定: 3） */
    maxErosions: z.number().optional(),
    /** 無傷クリア時メッセージ（2026-06-12 R0 追加・実装既定: '耐えきった...軽傷で済んだ'） */
    successMessage: z.string().optional(),
    /** 一部欠損時メッセージ（2026-06-12 R0 追加・実装既定: '一部が侵食された...'） */
    partialMessage: z.string().optional(),
    /** fail 時メッセージ（2026-06-12 R0 追加・実装既定: '存在が喰われた...'） */
    failMessage: z.string().optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ17: 逃走タイミング（TimingEscape）
// 2026-06-12 R0 監査: 実装（Template17TimingEscape.jsx）準拠キー（horrorMode/maxDarkness）を optional 追加
//（minor・schemaVersion 1 維持・非破壊）。不在時は実装側既定値が効くため .default() は付けない。
export const TimingEscapeConfigSchema = z.object({
    templateId: z.literal(17),
    title: z.string().optional(),
    instruction: z.string().default('タイミングよくタップして逃げろ！'),
    requiredSuccesses: z.number().min(1).max(20).default(5),
    totalAttempts: z.number().min(1).max(30).default(8),
    windowSize: z.number().min(50).max(500).default(200),
    indicatorSpeed: z.number().min(500).max(5000).default(1500),
    perfectWindowSize: z.number().min(20).max(200).default(80),
    chaserVisual: z
        .object({
        /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
        image: AssetRefSchema.optional(),
        emoji: z.string().default('👹'),
        name: z.string().default('追跡者')
    })
        .optional(),
    playerVisual: z
        .object({
        /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
        image: AssetRefSchema.optional(),
        emoji: z.string().default('🏃'),
        name: z.string().default('あなた')
    })
        .optional(),
    distanceDisplay: z
        .object({
        showDistance: z.boolean().default(true),
        initialDistance: z.number().default(50),
        successGain: z.number().default(15),
        perfectGain: z.number().default(25),
        failPenalty: z.number().default(20),
        escapeDistance: z.number().default(100),
        caughtDistance: z.number().default(0)
    })
        .optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    successSFX: AssetRefSchema.optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    failSFX: AssetRefSchema.optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    perfectSFX: AssetRefSchema.optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    caughtSFX: AssetRefSchema.optional(),
    /** @deprecated 実装未消費（2026-06-12 R0 監査）。v2 で削除予定。 */
    escapeSFX: AssetRefSchema.optional(),
    /** ホラー演出（失敗累積の暗転・赤縁取り）の有効化（2026-06-12 R0 追加・実装既定: true） */
    horrorMode: z.boolean().optional(),
    /** 暗転オーバーレイの最大不透明度（2026-06-12 R0 追加・実装既定: 0.85） */
    maxDarkness: z.number().optional(),
    assets: AssetIdRefSchema.optional()
});
// テンプレ18: ホラーQTE（HorrorQTE）
export const HorrorQTEConfigSchema = z.object({
    templateId: z.literal(18),
    title: z.string().optional(),
    instruction: z.string().default('...'),
    requiredSuccesses: z.number().min(1).max(20).default(4),
    totalAttempts: z.number().min(1).max(20).default(6),
    windowSizeMs: z.number().min(200).max(2000).default(600),
    perfectWindowMs: z.number().min(50).max(500).default(200),
    intervalMs: z.number().min(500).max(5000).default(2000),
    promptText: z.string().default('逃げろ'),
    successText: z.string().default('...'),
    failText: z.string().default('...'),
    horror: z
        .object({
        pulseColor: z.string().default('rgba(139, 0, 0, 0.6)'),
        flashOnFail: z.boolean().default(true),
        shakeOnFail: z.boolean().default(true),
        darkenOnStart: z.boolean().default(true),
        showHeartbeat: z.boolean().default(true)
    })
        .optional(),
    assets: AssetIdRefSchema.optional()
});
export const MrLiquidVariantSchema = z.enum([
    'film_search',
    'mirror_align',
    'water_battle',
    'fire_line'
]);
const MrLiquidMapSpotSchema = z.object({
    id: z.string(),
    label: z.string(),
    keyword: z.string().optional(),
    ability: z.string().optional()
});
export const MrLiquidConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(19),
    variant: MrLiquidVariantSchema.default('film_search'),
    title: z.string().default('Mr.リキッド能力発動'),
    abilityLabel: z.string().default('液膜探索'),
    instructionText: z.string().default('現地の痕跡に液術を合わせてください。'),
    clueKey: z.string().optional(),
    successText: z.string().default('手がかりが液面に浮かび上がった。'),
    map: z
        .object({
        title: z.string().default('水守商店街 簡易マップ'),
        currentIndex: z.number().int().min(0).max(9).default(0),
        spots: z.array(MrLiquidMapSpotSchema).default([])
    })
        .optional(),
    filmSearch: z
        .object({
        sequence: z.array(z.string()).default(['濡れた縁', '浮いた文字', '屋号の芯']),
        decoys: z.array(z.string()).default(['乾いた節', '欠けた釘'])
    })
        .optional(),
    mirrorAlign: z
        .object({
        targetAngle: z.number().min(-180).max(180).default(45),
        tolerance: z.number().min(1).max(45).default(10),
        symbols: z.array(z.string()).default(['水守屋', '北東', '井戸'])
    })
        .optional(),
    waterBattle: z
        .object({
        directions: z.array(z.enum(['up', 'down', 'left', 'right'])).default(['up', 'right', 'down', 'left']),
        targetLabel: z.string().default('灯籠の火種')
    })
        .optional(),
    fireLine: z
        .object({
        fires: z.array(z.string()).default(['表の火元', '路地の火元', '蔵前の火元']),
        lineLabel: z.string().default('水のライン')
    })
        .optional()
});
export const SomaCallingFireVariantSchema = z.enum([
    'river_fragments',
    'soma_distortion',
    'voice_restore'
]);
export const SomaCallingFireConfigSchema = BaseConfigSchema.extend({
    templateId: z.literal(20),
    variant: SomaCallingFireVariantSchema.default('river_fragments'),
    title: z.string().default('杣川の呼び火'),
    instructionText: z.string().default('九十九商会端末の反応をたどってください。'),
    submitButtonLabel: z.string().default('結果を送る'),
    successText: z.string().optional(),
    fragments: z.array(z.string()).default(['いろ', 'こえ', 'へる', 'いたい', 'のこる']),
    observedClues: z
        .array(z.string())
        .default(['clue_repainted_color', 'clue_reduced_voices', 'clue_pain_remains']),
    restoredFragments: z.array(z.string()).default(['そま行こ', 'そまで遊ぼ']),
    revealedNameHint: z.string().default('そま')
});
export const MinigameConfigSchema = z.discriminatedUnion('templateId', [
    InvestigationConfigSchema,
    FingerprintConfigSchema,
    CipherConfigSchema,
    TimelineConfigSchema,
    EvidenceBoardConfigSchema,
    ComparisonConfigSchema,
    ContradictionConfigSchema,
    ChaseConfigSchema,
    IntenseChaseConfigSchema,
    InvestigationV2ConfigSchema,
    ContradictionV2ConfigSchema,
    InterrogationConfigSchema,
    DeductionConfigSchema,
    CipherV2ConfigSchema,
    DoorSelectionConfigSchema,
    ExistenceErosionConfigSchema,
    TimingEscapeConfigSchema,
    HorrorQTEConfigSchema,
    MrLiquidConfigSchema,
    SomaCallingFireConfigSchema
]);
// 互換用: 未対応テンプレは暫定で any 許容
export const MinigameConfigSchemaLoose = MinigameConfigSchema.or(z.record(z.string(), z.any()));
//# sourceMappingURL=minigameConfig.js.map